import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { RootState } from '@/state/store';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { logout } from '@/state/authSlice';
import { listenCrossTabMessages, CrossTabMessage } from '@/utils/crossTabSync';

/**
 * CrossTabSyncModal
 *
 * Added based on Dinisuru's request:
 * In multi-tab browsing, if the user switches active company in another tab,
 * this tab intercepts the cross-tab event and prompts the user to reload.
 * This guarantees that forms and operations in this tab cannot mistakenly post
 * transactions into the previous or wrong company.
 */
export const CrossTabSyncModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const session  = useSelector((s: RootState) => s.session.session);

  const [conflictCompany, setConflictCompany] = useState<{
    companyId: string;
    companyCode: string;
    companyName: string;
  } | null>(null);

  useEffect(() => {
    const unsubscribe = listenCrossTabMessages((msg: CrossTabMessage) => {
      if (msg.type === 'AUTH_LOGOUT') {
        // Another tab logged out — clean up this tab immediately
        dispatch(logout());
        window.location.href = '/login';
        return;
      }

      if (msg.type === 'COMPANY_CHANGED') {
        // If this tab already has a company and the other tab changed to a different company:
        if (session?.companyId && msg.companyId !== session.companyId) {
          setConflictCompany({
            companyId:   msg.companyId,
            companyCode: msg.companyCode,
            companyName: msg.companyName,
          });
        }
      }
    });

    return () => unsubscribe();
  }, [dispatch, session?.companyId]);

  if (!conflictCompany) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cross-tab-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/75 backdrop-blur-sm animate-fade-in"
    >
      <div className="w-full max-w-md rounded-xl bg-surface dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-2xl p-6 text-left space-y-4">
        <div className="flex items-start gap-3.5">
          <div className="p-2.5 rounded-lg bg-amber-500/10 text-amber-500 flex-shrink-0">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h2 id="cross-tab-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Corporate Context Changed
            </h2>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
              Your active company was switched to{' '}
              <strong className="text-slate-900 dark:text-slate-100">
                {conflictCompany.companyName} ({conflictCompany.companyCode})
              </strong>{' '}
              in another browser tab.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200/70 dark:border-amber-900/50 p-3 text-xs text-amber-800 dark:text-amber-300">
          To protect ledger integrity and prevent posting entries to the wrong company, this tab must reload to sync with your active session.
        </div>

        <div className="pt-2 flex justify-end">
          <button
            onClick={() => window.location.reload()}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium text-xs shadow-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Reload Tab Now
          </button>
        </div>
      </div>
    </div>
  );
};
